import React, { useState, ReactElement } from 'react';
import { message } from 'antd'
import { Input } from '@/components/ui'
import { PlusOutlined } from '@ant-design/icons';
import { Category } from '@/types/events';
import CustomMultiSelect from './CustomMultiSelect';
import { api } from '../../lib/apiClient';

interface CategoryMultiSelectProps {
  categories: Category[];
  selectedCategoryIds: string[];
  onChange: (categoryIds: string[]) => void;
  placeholder?: string;
}

const CategoryMultiSelect: React.FC<CategoryMultiSelectProps> = ({
  categories,
  selectedCategoryIds,
  onChange,
  placeholder = "Select or create categories"
}) => {
  const [categoryItems, setCategoryItems] = useState<Category[]>(categories);
  const [newCategoryName, setNewCategoryName] = useState<string>('');
  const [creatingCategory, setCreatingCategory] = useState<boolean>(false);

  // Handle creating a new category
  const handleCreateCategory = () => {
    if (!newCategoryName || creatingCategory) return;
    
    // Set loading state
    setCreatingCategory(true);
    
    // Call the API to create a new category
    const categoryData = { category: { name: newCategoryName } };
    
    api.post<{success: boolean, category: Category}>('/admin/categories', categoryData)
    .then(response => {
      // Properly type the response
      const responseData = response as unknown as {success: boolean, category: Category};
      const newCategory = responseData.category;
      
      // Add to local state
      setCategoryItems([...categoryItems, newCategory]);
      
      // Select the new category
      onChange([...selectedCategoryIds, newCategory.id]);
      
      // Show success message
      message.success(`Category "${newCategoryName}" added successfully`);
      
      // Reset input
      setNewCategoryName('');
    })
    .catch(error => {
      console.error('Error creating category:', error);
      message.error(`Failed to create category: ${error.message || 'Unknown error'}`);
    })
    .finally(() => {
      setCreatingCategory(false);
    });
  };

  // Convert categories to the format expected by CustomMultiSelect
  const options = categoryItems.map(category => ({
    label: category.name,
    value: category.id || '',
    initials: category.name.charAt(0).toUpperCase(),
    color: '#4CAF50'  // Green color for categories
  }));

  // Custom dropdown render to add "Create new category" option
  const renderDropdown = (menu: ReactElement): ReactElement => {
    return (
      <>
        {menu}
        <div
          style={{
            display: 'flex',
            flexWrap: 'nowrap',
            padding: 8,
            borderTop: '1px solid #e8e8e8',
          }}
        >
          <Input
            style={{ flex: 'auto' }}
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            placeholder="New category name"
            size="small"
          />
          <a
            style={{
              flex: 'none',
              padding: '8px',
              display: 'block',
              cursor: creatingCategory ? 'not-allowed' : 'pointer',
              opacity: creatingCategory ? 0.5 : 1
            }}
            onClick={handleCreateCategory}
          >
            <PlusOutlined /> {creatingCategory ? 'Adding...' : 'Add category'}
          </a>
        </div>
      </>
    );
  };

  return (
    <CustomMultiSelect
      options={options}
      selectedValues={selectedCategoryIds}
      onChange={onChange}
      placeholder={placeholder}
      colorPalette={['#4CAF50', '#8BC34A', '#CDDC39', '#FFC107']} // Green palette for categories
      dropdownRender={renderDropdown}
    />
  );
};

export default CategoryMultiSelect;
